import { z } from 'zod'

import { asEnumType, asObjectType } from '@gqloom/zod'

// TODO(@jliocsar): Should probably be moved to @hapana/shared/constants
import { NotificationType } from '../../backend/services/constants'
import { userTypeSchema } from '../common/user-types.schema'

export const FileSupportedVariant = {
  Original: 'original',
} as const

export const FileVariant = {
  Original: 'original',
} as const

export const FileUploadConfigFieldStatus = {
  Processing: 'PROCESSING',
  Ready: 'READY',
} as const

export const fileUploadConfigFieldStatusSchema = z
  .enum(['PROCESSING', 'READY'])
  .register(asEnumType, {
    name: 'FileUploadConfigFieldStatus',
  })

export const FileUploadConfigFieldPendingStatus = {
  Processing: 'PROCESSING',
  Uploading: 'UPLOADING',
} as const

export const fileUploadConfigFieldPendingStatusSchema = z
  .enum(['PROCESSING', 'UPLOADING'])
  .register(asEnumType, {
    name: 'FileUploadConfigFieldPendingStatus',
  })

export const FileUploadSupportedType = {
  Image: 'image',
  Video: 'video',
  Document: 'document',
  Font: 'font',
  Csv: 'csv',
} as const

export const fileConfigFieldSchema = z.object({
  status: fileUploadConfigFieldStatusSchema,
  lastFailure: z
    .object({
      tempUrl: z.string(),
      failedAt: z.coerce.date(),
      errorMessage: z.string(),
      uploadRequestedBy: z.uuid(),
    })
    .optional(),
  pending: z
    .object({
      status: fileUploadConfigFieldPendingStatusSchema,
      tempUrl: z.string(),
      uploadRequestedAt: z.coerce.date(),
      uploadRequestedBy: z.uuid(),
    })
    .optional(),
})

export const fileVersionSchema = z
  .object({
    url: z.string(),
    type: z.enum(FileVariant).register(asEnumType, {
      name: 'FileVariant',
      description: 'Variant of a file',
    }),
  })
  .register(asObjectType, {
    name: 'FileVersion',
  })

// TODO(@jliocsar): This _should_ be type-safe
// (i.e. only suggest the right tableName->columnName+variants combinations)
export const generateFileSignedUrlInputSchema = z
  .object({
    id: z.uuid().describe('The ID of the entity'),
    fileName: z.string().describe('Name of the file being uploaded'),
    variants: z.array(z.string()).describe('The variants of the entity files'),
    formats: z.array(z.string()).describe('The formats of the entity files'),
    table: z
      .string()
      .regex(/^[a-zA-Z]+$/)
      .describe('Name of the table'),
  })
  .register(asObjectType, {
    name: 'GenerateFileUrlInput',
  })

export const generateFileUploadSignedUrlInputSchema = generateFileSignedUrlInputSchema
  .omit({
    variants: true,
    formats: true,
  })
  .extend({
    format: z.string().describe('Format of the file being uploaded'),
    clientId: z.string().uuid().optional().describe('Client ID for ClientFile table'),
    siteId: z.string().uuid().optional().describe('Site ID for ClientFile table'),
    metadata: z
      .record(z.string(), z.string())
      .optional()
      .describe('Additional metadata to include in signed URL headers'),
  })
  .register(asObjectType, {
    name: 'GenerateFileUploadUrlInput',
  })

export const generateFileUploadSignedUrlResponseSchema = z
  .object({
    tempUrl: z.string().describe('Signed URL of the temporary path'),
    requestedBy: z
      .uuid()
      .describe(
        'Value the server signed into x-goog-meta-requested-by. Client MUST echo this exact value as the header on PUT — any drift fails the V4 signature verification.'
      ),
    // userTypeSchema is registered as a `UserType` GraphQL enum elsewhere;
    // chaining `.describe()` here would strip the registration and produce a
    // duplicate `UserType` at federation weave-time. Description lives on the
    // sibling `requestedBy` field which carries the same client-must-echo
    // semantics.
    userType: userTypeSchema,
  })
  .register(asObjectType, {
    name: 'GenerateFileUploadUrlResponse',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

export const filePublicSignedUrlSchema = generateFileUploadSignedUrlInputSchema.pick({
  format: true,
})

export const tempFileMetadataHeadersSchema = z
  .object({
    'requested-by': z.uuid(),
    'user-type': userTypeSchema,
    'corporate-slug': z.string().optional(),
    'client-id': z.uuid().optional(),
    'site-id': z.uuid().optional(),
  })
  .transform((headers) => ({
    id: headers['requested-by'],
    type: headers['user-type'],
    corporateSlug: headers['corporate-slug'],
    clientId: headers['client-id'],
    siteId: headers['site-id'],
  }))

export function createVersionedFileConfigFieldSchema<
  TVersionSchema extends typeof fileVersionSchema,
>(versionSchema: TVersionSchema) {
  return fileConfigFieldSchema.extend({
    versions: z.array(versionSchema).optional(),
  })
}

export type GenerateFileSignedUrlInputSchema = z.infer<typeof generateFileSignedUrlInputSchema>
export type GenerateFileUploadSignedUrlInputSchema = z.infer<
  typeof generateFileUploadSignedUrlInputSchema
>
export type GenerateFileUploadSignedUrlResponseSchema = z.infer<
  typeof generateFileUploadSignedUrlResponseSchema
>

export type FileVersionSchema = z.infer<typeof fileVersionSchema>
export type FileConfigFieldSchema = z.infer<ReturnType<typeof createVersionedFileConfigFieldSchema>>

export type TempFileMetadataHeadersSchema = z.infer<typeof tempFileMetadataHeadersSchema>

export type FileUploadSupportedTypeNameMap = typeof FileUploadSupportedType
export type FileUploadSupportedTypeValue =
  FileUploadSupportedTypeNameMap[keyof FileUploadSupportedTypeNameMap]

export interface FileUploadFieldNotificationConfig {
  type: NotificationType
  id(context: unknown): unknown
  params(
    context: unknown,
    ...args: any[]
  ): {
    title: string
    body: string
    data: Record<string, unknown>
  }
}

export interface FileUploadFieldConfigBase {
  dbKey: 'db' | 'adminDb'
  maxFileSize: number
  columnName: string
  idField: string
  allowedFormats: string[]
  isPublic?: boolean
  notification?: FileUploadFieldNotificationConfig
  /**
   * Set to true for tables with composite/interleaved primary keys.
   * When true, uses SQL UPDATE instead of Spanner's update() method,
   * allowing updates by idField only without needing all PK columns.
   */
  isInterleaved?: boolean
  /**
   * Kept only so readers resolve the column; the field is written through a direct-upload target
   * whose authorize rule and dimension check this signed-URL path cannot apply.
   */
  readOnly?: boolean
}

export interface FileUploadServiceBase {
  generateTempPath(
    input: {
      table: string
      fileName: string
      id: string
      format: string
    },
    entityFieldConfig: FileUploadFieldConfigBase
  ): string | Promise<string>
}
